import { useCallback } from "react";

/**
 * A rich clipboard payload writes multiple representations of the same content
 * so paste targets pick the format they understand best. `plainText` is always
 * required (and used by every fallback path); `html` is optional and only
 * written when the async Clipboard API and `ClipboardItem` are available.
 */
type RichClipboardPayload = {
  plainText: string;
  html?: string;
};

export type ClipboardPayload = string | RichClipboardPayload;

function normalizePayload(input: ClipboardPayload): RichClipboardPayload {
  if (typeof input === "string") {
    return { plainText: input };
  }
  return input;
}

async function writeRichClipboard(payload: RichClipboardPayload): Promise<boolean> {
  if (!payload.html || !navigator.clipboard?.write || !globalThis.ClipboardItem) {
    return false;
  }
  try {
    const item = new globalThis.ClipboardItem({
      "text/plain": new Blob([payload.plainText], { type: "text/plain" }),
      "text/html": new Blob([payload.html], { type: "text/html" }),
    });
    await navigator.clipboard.write([item]);
    return true;
  } catch {
    return false;
  }
}

async function writePlainClipboard(text: string): Promise<boolean> {
  if (!navigator.clipboard?.writeText) {
    return false;
  }
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function copyViaTextareaFallback(text: string): void {
  // Fallback for environments where the async Clipboard API is unavailable or blocked
  // (e.g. older browsers, non-secure contexts, or permission-restricted webviews).
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "0";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, text.length);
  try {
    document.execCommand("copy");
  } finally {
    document.body.removeChild(textarea);
  }
}

/**
 * Accepts either a plain string (legacy callers) or a `{ plainText, html? }`
 * payload. When `html` is provided and the async Clipboard API supports
 * `ClipboardItem`, both representations are written in one clipboard entry so
 * paste targets like Word and Google Docs receive formatted output while code
 * editors and markdown-aware apps still receive the original plain-text source.
 *
 * https://developer.mozilla.org/en-US/docs/Web/API/Clipboard_API
 */
export const useCopyToClipboard = () => {
  return useCallback(async (input: ClipboardPayload) => {
    const payload = normalizePayload(input);

    if (await writeRichClipboard(payload)) {
      return;
    }

    if (await writePlainClipboard(payload.plainText)) {
      return;
    }

    copyViaTextareaFallback(payload.plainText);
  }, []);
};
